import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { SupplierClosures } from "./closures";
import { SupplierOpeningHours } from "./opening-hours";

import {
  CheckboxField,
  FormError,
  NumberInputField,
  SheetSection,
  SideSheet,
  TextInputField,
  notify,
} from "@/components/kit";
import { PRODUCT_KEY } from "@/hooks/api/product/keys";
import { useCreateSupplier, useUpdateSupplier } from "@/hooks/api/supplier";
import {
  SUPPLIER_FORM_SCHEMA,
  SupplierFormData,
  toOpeningHours,
  toSupplierFormValues,
} from "@/schemas/supplier-form.schema";
import { Supplier, UpdateSupplier } from "@/types/supplier";
import { getApiErrorMessage } from "@/utils/api-error";

interface SupplierSheetProps {
  supplier: Supplier | null;
  isOpen: boolean;
  onClose: () => void;
}

export const SupplierSheet = ({
  supplier,
  isOpen,
  onClose,
}: SupplierSheetProps) => {
  const form = useForm<SupplierFormData>({
    resolver: zodResolver(SUPPLIER_FORM_SCHEMA),
    mode: "all",
    defaultValues: toSupplierFormValues(null),
  });
  const { dirtyFields, isDirty } = form.formState;
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const queryClient = useQueryClient();
  const mutation = supplier ? update : create;

  useEffect(() => {
    if (!isOpen) return;

    form.reset(toSupplierFormValues(supplier));
    create.reset();
    update.reset();
  }, [isOpen, supplier]);

  const handleSubmit = (data: SupplierFormData) => {
    const openingHours = toOpeningHours(data);

    if (!supplier) {
      create.mutate(
        {
          name: data.name,
          isActive: data.isActive,
          margin: data.margin,
          cap: data.cap,
          openingHours,
        },
        {
          onSuccess: () => {
            notify.success("Supplier added");
            onClose();
          },
        },
      );

      return;
    }

    const changes: UpdateSupplier = {
      id: supplier.id,
      name: data.name,
      ...(dirtyFields.isActive && { isActive: data.isActive }),
      ...(dirtyFields.margin && { margin: data.margin }),
      ...(dirtyFields.cap && { cap: data.cap }),
      ...((dirtyFields.alwaysOpen || dirtyFields.days) && { openingHours }),
    };

    update.mutate(changes, {
      onSuccess: () => {
        // a new margin or cap reprices the supplier's products
        if (changes.margin !== undefined || changes.cap !== undefined) {
          queryClient.invalidateQueries({ queryKey: [PRODUCT_KEY] });
        }
        notify.success("Supplier saved");
        onClose();
      },
    });
  };

  return (
    <FormProvider {...form}>
      <SideSheet
        description={supplier?.slug}
        isOpen={isOpen}
        isSubmitDisabled={!!supplier && !isDirty}
        isSubmitting={mutation.isPending}
        submitLabel={supplier ? "Save" : "Add supplier"}
        title={supplier ? "Edit supplier" : "Add supplier"}
        onClose={onClose}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormError
          message={
            mutation.error
              ? getApiErrorMessage(
                  mutation.error,
                  "Could not save the supplier, please try again",
                )
              : null
          }
        />
        <SheetSection title="Details">
          <TextInputField<SupplierFormData>
            isRequired
            label="Name"
            name="name"
            placeholder="Name of the supplier"
          />
          <CheckboxField<SupplierFormData> label="Active" name="isActive" />
        </SheetSection>
        <SheetSection
          description="Product price is the supplier price plus the margin, adding at most the cap. Saving a change reprices this supplier's products"
          title="Pricing"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberInputField<SupplierFormData>
              label="Margin (%)"
              name="margin"
              placeholder="35"
            />
            <NumberInputField<SupplierFormData>
              label="Cap (AED)"
              name="cap"
              placeholder="10.00"
            />
          </div>
        </SheetSection>
        <SheetSection
          description="Delivery estimates only count the time the supplier is open"
          title="Opening hours"
        >
          <SupplierOpeningHours />
        </SheetSection>
        <SheetSection
          description="Days the supplier is closed, both dates included"
          title="Closures"
        >
          {supplier ? (
            <SupplierClosures supplierId={supplier.id} />
          ) : (
            <p className="text-muted text-sm">
              Add the supplier first, then its closures
            </p>
          )}
        </SheetSection>
      </SideSheet>
    </FormProvider>
  );
};
